import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {IPopupProps, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import * as classnames from 'classnames/bind';
import * as React from 'react';
import {CSSProperties} from 'react';

const styles = require('@mintdata/mint-kit/impl/components/tooltip/Tooltip.less');
const cx = classnames.bind(styles);

export enum TooltipArrowPosition
{
    TopLeft = 'TopLeft',
    TopMiddle = 'TopMiddle',
    TopRight = 'TopRight',
    BottomLeft = 'BottomLeft',
    BottomMiddle = 'BottomMiddle',
    BottomRight = 'BottomRight',
    LeftTop = 'LeftTop',
    LeftMiddle = 'LeftMiddle',
    LeftBottom = 'LeftBottom',
    RightTop = 'RightTop',
    RightMiddle = 'RightMiddle',
    RightBottom = 'RightBottom',
}

export interface ITooltipProps
{
    targetClassName?:string
    tooltipClassName?:string;
    content:string | JSX.Element;
    popoverProps?:Partial<IPopupProps>;
    showArrow?:boolean;
    arrowPosition?:TooltipArrowPosition;
    hoverOpenDelay?:number;
    transitionDuration?:number;
    openOnTargetFocus?:boolean;
    closeOnTargetClick?:boolean;
}

export interface ITooltipState
{
    isPopoverOpen:boolean;
}

export class Tooltip extends React.Component<ITooltipProps, ITooltipState>
{
    public static defaultProps = {
        popoverProps: {},
        showArrow: true,
        arrowOffset: 0,
        hoverOpenDelay: 150,
        hoverCloseDelay: 0,
        transitionDuration: 150,
        openOnTargetFocus: true,
        closeOnTargetClick: false
    };

    public state:ITooltipState = {
        isPopoverOpen: false
    };

    private _popoverId:string = IdUtil.newGUID();
    private _popoverSource:HTMLElement | null;

    private _refHandlers = {
        popoverSource: (ref:HTMLElement | null) =>
        {
            this._popoverSource = ref;
        }
    };

    public render():JSX.Element
    {
        const {
            targetClassName,
            tooltipClassName,
            popoverProps,
            content,
            openOnTargetFocus,
            children
        } = this.props;

        const {isPopoverOpen} = this.state;

        const tooltipStyles = this._computeStyles();
        const arrowClassName = this._getArrowClassName();

        const targetTabIndex = openOnTargetFocus ? -1 : undefined;

        return (
            <span
                ref={this._refHandlers.popoverSource}
                tabIndex={targetTabIndex}
                className={targetClassName}
                onMouseEnter={this._handleTargetMouseEnter}
                onMouseLeave={this._handleTargetMouseLeave}
                onClick={this._handleTargetClick}
                onFocus={this._handleTargetFocus}
                onBlur={this._handleTargetBlur}
            >
                {children}
                {
                    isPopoverOpen && content &&
                    <Popover
                        sourceAnchor={SourceAnchor.CenterTop}
                        popupAnchor={PopupAnchor.CenterBottom}
                        {...popoverProps}
                        className={cx(arrowClassName && `${arrowClassName}Popover`, popoverProps.className)}
                        id={this._popoverId}
                        popupSource={this._popoverSource}
                        onDismissed={this._handlePopoverDismissed}
                        disablePointerEvents={true}
                    >
                        <div
                            className={cx('tooltip', arrowClassName, tooltipClassName)}
                            style={tooltipStyles}
                        >
                            {content}
                        </div>
                    </Popover>
                }
            </span>
        );
    }

    private _getArrowClassName():string
    {
        const {showArrow, arrowPosition, popoverProps} = this.props;

        if( !showArrow )
        {
            return '';
        }

        if( arrowPosition )
        {
            return `arrow${arrowPosition}`;
        }

        const popupAnchor = popoverProps && popoverProps.popupAnchor || PopupAnchor.CenterBottom;
        let position = '';

        switch( popupAnchor )
        {
            case PopupAnchor.LeftTop:
                position = TooltipArrowPosition.TopLeft;
                break;
            case PopupAnchor.RightTop:
                position = TooltipArrowPosition.TopRight;
                break;
            case PopupAnchor.CenterTop:
                position = TooltipArrowPosition.TopMiddle;
                break;
            case PopupAnchor.LeftBottom:
                position = TooltipArrowPosition.BottomLeft;
                break;
            case PopupAnchor.RightBottom:
                position = TooltipArrowPosition.BottomRight;
                break;
            case PopupAnchor.CenterBottom:
                position = TooltipArrowPosition.BottomMiddle;
                break;
            case PopupAnchor.LeftMiddle:
                position = TooltipArrowPosition.LeftMiddle;
                break;
            case PopupAnchor.RightMiddle:
                position = TooltipArrowPosition.RightMiddle;
                break;
        }

        return position ? `arrow${position}` : '';
    }

    private _computeStyles = ():CSSProperties =>
    {
        const {transitionDuration, hoverOpenDelay} = this.props;

        return {
            animationDuration: `${transitionDuration}ms`,
            animationDelay: `${hoverOpenDelay}ms`
        };
    };

    private _handleTargetMouseEnter = () =>
    {
        this.setState({isPopoverOpen: true});
    };

    private _handleTargetMouseLeave = () =>
    {
        this.setState({isPopoverOpen: false});
    };

    private _handleTargetClick = () =>
    {
        const {closeOnTargetClick} = this.props;

        if( closeOnTargetClick )
        {
            this.setState({isPopoverOpen: false});
        }
    };

    private _handleTargetFocus = () =>
    {
        const {openOnTargetFocus} = this.props;

        if( openOnTargetFocus )
        {
            this.setState({isPopoverOpen: true});
        }
    };

    private _handleTargetBlur = () =>
    {
        const {openOnTargetFocus} = this.props;

        if( openOnTargetFocus )
        {
            this.setState({isPopoverOpen: false});
        }
    };

    private _handlePopoverDismissed = () =>
    {
        this.setState({isPopoverOpen: false});
    };
}

